import { Button } from "@components/ui/button";
import { Field, FieldError, FieldLabel } from "@components/ui/field";
import Logo from "@components/ui/logo";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import { useSignupContext, useSignupRoutes } from "../context";
import { AnimatePresence, motion } from "motion/react";
import AllEarsTitle from "@components/AllEarsTitle";
import EntitySearch from "./entity-search";
import SignupImage from "@components/SignupImage";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { useHistory } from "react-router-dom";
import UseCases from "./use-cases";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

export default function SignupIndex() {
  const { t } = useLingui();
  const history = useHistory();

  // Context
  const {
    companyName,
    companyDescription,
    fullCompanyName,
    setCompanyName,
    websiteUrl,
    setWebsiteUrl,
    setCompanyDescription,
    setFullCompanyName,
    useCases,
    setUseCases,
    customUseCase,
    setCustomUseCase,
  } = useSignupContext();
  const signupRoutes = useSignupRoutes();

  // Local state
  const [queryBuilderErrored, setQueryBuilderErrored] =
    useState<boolean>(false);

  // Validation
  const formSchema = useMemo(() => {
    return z.object({
      companyName: z.string({
        error: t`Vänligen fyll i ditt bolags namn`,
      }),
      fullCompanyName: z.string().optional(),
      companyDescription: z.string({
        error: t`Vänligen fyll i en beskrivning för ditt bolag`,
      }),
      companyURL: z.string({
        error: t`Vänligen fyll i ditt bolags hemsida`,
      }),
      useCases: z.array(z.string()).min(1, {
        error: t`Vänligen välj minst 1 användningsområde`,
      }),
    });
  }, [t]);

  // Form state
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyDescription: companyDescription ?? "",
      fullCompanyName: fullCompanyName,
      companyName: companyName ?? "",
      companyURL: websiteUrl ?? "",
      useCases: useCases ?? [],
    },
  });

  const [_companyName, _companyDescription, _companyURL, _useCases] =
    form.watch(["companyName", "companyDescription", "companyURL", "useCases"]);

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    setCompanyName(values.companyName);
    setCompanyDescription(values.companyDescription);
    setWebsiteUrl(values.companyURL);
    setUseCases(values.useCases);
    if (values.fullCompanyName) {
      setFullCompanyName(values.fullCompanyName);
    }
    history.push(signupRoutes.SIGNUP_INFO);

    postTrackingData(EVENT.ONBOARDING_USE_CASES_SELECTED, {
      selected_use_cases: values.useCases,
      use_case_count: values.useCases.length,
    });
  };

  return (
    <>
      <AllEarsTitle title={t`Vad vill du bevaka?`} />

      <div className="md:h-svh grid md:grid-cols-2">
        <div className="p-7 md:p-14 flex flex-col gap-[20svh] overflow-y-auto h-full">
          <Logo width={80} className="self-start" />

          <div className="w-full space-y-6">
            <div className="space-y-1.5 text-balance max-w-lg mx-auto">
              <h1 className="text-2xl font-semibold">
                <Trans>Vad vill du bevaka?</Trans>
              </h1>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Vi fångar upp omnämnanden i realtid som du annars missar.
                </Trans>
              </p>
            </div>

            <form
              id="form-signup"
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="flex flex-col gap-6 max-w-lg mx-auto">
                <Controller
                  control={form.control}
                  name="companyURL"
                  render={({ field, fieldState }) => {
                    const id = "company-info";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>
                            Fyll i webbplats för varumärke eller organisation
                          </Trans>
                        </FieldLabel>
                        <EntitySearch
                          {...field}
                          id={id}
                          onSuccess={(result) => {
                            form.setValue("companyName", result.companyName);
                            form.setValue(
                              "fullCompanyName",
                              result.fullCompanyName
                            );
                            form.setValue(
                              "companyDescription",
                              result.companyDescription
                            );
                            setQueryBuilderErrored(false);

                            postTrackingData(
                              EVENT.ONBOARDING_SUCCESSFUL_URL_SUBMISSION,
                              {
                                input_url: result.url,
                                url_valid: true,
                              }
                            );
                          }}
                          onError={(url, error) => {
                            setQueryBuilderErrored(true);
                            if (url) {
                              form.setValue("companyURL", url);
                            }

                            postTrackingData(
                              EVENT.ONBOARDING_UNSUCCESSFUL_URL_SUBMISSION,
                              {
                                input_url: url,
                                url_valid: false,
                                error_type: error,
                              }
                            );
                          }}
                        />

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                {queryBuilderErrored && (
                  <>
                    <Controller
                      control={form.control}
                      name="companyName"
                      render={({ field, fieldState }) => {
                        const id = "company-name";

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>Bolagsnamn</Trans>
                            </FieldLabel>

                            <Input
                              {...field}
                              id={id}
                              aria-invalid={fieldState.invalid}
                              placeholder={t`Acme INC`}
                            />

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />

                    <Controller
                      control={form.control}
                      name="companyDescription"
                      render={({ field, fieldState }) => {
                        const id = "company-description";

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>Bolags beskrivning</Trans>
                            </FieldLabel>

                            <Textarea
                              {...field}
                              id={id}
                              rows={6}
                              className="resize-none"
                              aria-invalid={fieldState.invalid}
                              placeholder={t`Det här bolaget jobbar med att...`}
                            />

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />
                  </>
                )}
              </div>

              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Controller
                    control={form.control}
                    name="useCases"
                    render={({ field, fieldState }) => (
                      <Field className="gap-4">
                        <FieldLabel className="text-sm font-medium max-w-lg mx-auto">
                          <Trans>Vad vill du använda All Ears till?</Trans>
                        </FieldLabel>

                        <UseCases
                          value={field.value}
                          onChange={field.onChange}
                          customUseCase={customUseCase}
                          onCustomUseCaseChange={setCustomUseCase}
                        />

                        {fieldState.invalid && (
                          <FieldError
                            className="text-sm font-medium max-w-lg mx-auto"
                            errors={[fieldState.error]}
                          />
                        )}
                      </Field>
                    )}
                  />
                </motion.div>
              </AnimatePresence>

              <AnimatePresence>
                {_companyName && _companyDescription && _companyURL && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex justify-end max-w-lg mx-auto"
                  >
                    <Button
                      type="submit"
                      form="form-signup"
                      className="self-end"
                      disabled={!_useCases.length}
                    >
                      <Trans>Nästa</Trans>
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>

        <SignupImage />
      </div>
    </>
  );
}
